import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { MembraneProtection } from "../../../../../http/border/types/membraneProtections";
import { useBorderSystemsApi } from "../../../../../http/border/useBorderSystems";
import { useMembraneProtectionsApi } from "../../../../../http/border/useMembraneProtections";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface MembraneProtectionFormBody {
  item: SearchAdminItem | null;
  systemIds: string[];
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface MembraneProtectionFormProps {
  membraneProtection?: MembraneProtection;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
}

const MembraneProtectionForm: FC<MembraneProtectionFormProps> = ({
  membraneProtection,
  isDisabled,
  isLoading,
  onClose,
}) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const aborter = useRef(new AbortController());

  const itemsApi = useItemsApi();
  const borderSystemsApi = useBorderSystemsApi();
  const membraneProtectionsApi = useMembraneProtectionsApi();

  const borderSystems = borderSystemsApi.useAdminBorderSystems({ limit: 100 }, aborter.current.signal);

  const defaultValues = {
    item: membraneProtection?.item,
    systemIds: membraneProtection?.systems.map((x) => x.id) ?? [],
    order: membraneProtection?.order ?? 50,
    visibility: membraneProtection?.visibility ?? "hidden",
  };

  const onSubmit = () => async (formData: MembraneProtectionFormBody) => {
    setIsSubmitting(true);

    if (membraneProtection?.item.id) {
      membraneProtectionsApi
        .updateAdminMembraneProtection(membraneProtection, {
          systemIds: formData.systemIds,
          order: formData.order!,
          visibility: formData.visibility,
        })
        .then(() => onClose())
        .catch((err) => dispatch(showErrorNotification(i18n, err)))
        .finally(() => setIsSubmitting(false));
    } else {
      try {
        await membraneProtectionsApi.insertAdminMembraneProtection({
          itemId: formData.item?.id ?? "",
          systemIds: formData.systemIds,
          order: formData.order!,
          visibility: formData.visibility,
        });
        onClose();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<MembraneProtectionFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={onSubmit}
    >
      {({ control, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [membraneProtection]);

        const systemIds = watch("systemIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                      // Fetch initial data on mount
                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultOptions);
                      }, []);

                      return (
                        <CustomAsyncSelect
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultOptions}
                          isDisabled={!!membraneProtection || isDisabled}
                          loadOptions={loadOptions}
                          isClearable
                          getOptionLabel={itemLabel}
                          getOptionValue={(x) => x.id}
                          isInvalid={fieldState.invalid}
                          onChange={(value) => setValue("item", value ?? null)}
                        />
                      );
                    }}
                  </FormField>
                );
              }}
            />
            <FormField label={i18n.translation.systems.plural}>
              <MultiCheckbox
                disabled={isSubmitting || isDisabled || isLoading}
                values={
                  borderSystems.data?.items.map((x) => ({
                    id: x.id,
                    title: x.name,
                    selected: systemIds.includes(x.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue("systemIds", checked ? [...systemIds, id] : systemIds.filter((x) => x !== id));
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default MembraneProtectionForm;
